/**
 * Trading-stream client (SPEC §7.1): `wss://…/v1/stream/trading` on the main thread.
 *
 * - First frame is `Hello{jwt}`; the gateway answers `Welcome` or closes with 4401.
 * - `Ping` every 5 s; `Pong` yields the browser↔gateway RTT and the gateway↔Rithmic RTT. No frame
 *   within 10 s of a ping (`Heartbeat`, CB4) means a half-open socket: it is dropped, the status
 *   turns `disconnected` and the client reconnects with a fresh token, so a phone never shows
 *   stale positions and P&L as live. `checkLiveness()` (resume, tab visible, network back) probes
 *   at once and resyncs.
 * - `Welcome.boot_id` identifies the gateway process; a new one (a restart: the session id is
 *   stable per user) resumes from 0, and `trading/state.ts` drops the old process's state (CB6).
 * - After every `Welcome`, `Resume{last_seq}` replays missed ordered events (from the ring start on
 *   a first connect) or yields a `Snapshot`; `trading/state.ts` applies them idempotently.
 * - Tokens are single-use and 60 s, so every (re)connect fetches a fresh one via `getToken`.
 * - Backoff is the md client's `ReconnectPolicy`: the ladder resets only after a connection stayed
 *   up `STABLE_CONNECTION_MS`, so a gateway that welcomes and then drops the socket is not
 *   reconnected every 250 ms.
 *
 * Market data never flows through here; the md socket lives in the render worker (Phase 3).
 */
import { create, fromBinary, toBinary } from "@bufbuild/protobuf";
import { EnvelopeSchema, type Envelope } from "../gen/helm/v1/envelope_pb";
import { HelloSchema, PingSchema, ResumeSchema } from "../gen/helm/v1/session_pb";
import { ReconnectPolicy } from "./backoff";
import { Heartbeat } from "./heartbeat";
import { nextAppliedSeq, nextLastSeq, replayAppliedStart } from "./resumeSeq";
import { useConnection } from "./store";

export type StreamEvent = Envelope;
export type StreamListener = (env: StreamEvent) => void;

export type TradingStreamOptions = {
  /** Base URL of the gateway, e.g. `ws://localhost:5173` (Vite proxy) or `wss://helm.ts.net`. */
  baseUrl: string;
  /** Mint a fresh single-use gateway JWT (calls the API). */
  getToken: () => Promise<string>;
  clientVersion: string;
  tabId: string;
  pingIntervalMs?: number;
  /** No frame this long after a ping: the socket is dropped (default 10 s). */
  pongTimeoutMs?: number;
};

const CLOSE_UNAUTHENTICATED = 4401;
/**
 * No `Welcome` this long after the socket was created: drop it and retry. The gateway itself
 * closes a socket without `Hello` after 5 s and may wait up to 10 s for a new session to decide its
 * links before `Welcome`, so this only fires for a socket that is dead before the heartbeat runs.
 */
export const WELCOME_TIMEOUT_MS = 20_000;

export class TradingStreamClient {
  private ws: WebSocket | null = null;
  private readonly heartbeat: Heartbeat;
  /** A connect attempt is between `connect()` and its socket's Welcome or close. */
  private connecting = false;
  private welcomeTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly policy = new ReconnectPolicy();
  private stopped = false;
  private readonly listeners = new Set<StreamListener>();
  private readonly pendingPings = new Map<string, number>();
  private reqCounter = 0;
  private financeReplayTarget: bigint | null = null;
  private readonly pendingApplied = new Set<bigint>();

  constructor(private readonly opts: TradingStreamOptions) {
    this.heartbeat = new Heartbeat({
      sendPing: () => this.sendPing(),
      onDead: (reason) => {
        this.dropSocket(reason);
      },
      ...(opts.pingIntervalMs !== undefined ? { intervalMs: opts.pingIntervalMs } : {}),
      ...(opts.pongTimeoutMs !== undefined ? { timeoutMs: opts.pongTimeoutMs } : {}),
    });
  }

  subscribe(listener: StreamListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  start(): void {
    this.stopped = false;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.connecting = false;
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    ws?.close(1000, "client stop");
    useConnection.getState().set({ status: "disconnected" });
  }

  /**
   * The app came back (resume, tab visible again, network back; CB4): a socket that looks open may
   * be half-open after the phone slept. Probe it now and resync (Resume from our last seq; the
   * reducer applies replays idempotently); with no socket, reconnect now instead of after the
   * backoff delay.
   */
  checkLiveness(): void {
    if (this.stopped) return;
    const ws = this.ws;
    if (ws !== null && ws.readyState === WebSocket.OPEN && this.heartbeat.isRunning) {
      this.heartbeat.probe();
      try {
        this.send({
          case: "resume",
          value: create(ResumeSchema, { lastSeq: useConnection.getState().appliedSeq }),
        });
      } catch {
        // Not open after all: the probe's deadline handles it.
      }
      return;
    }
    if (this.connecting) return;
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    void this.connect();
  }

  /**
   * Give up on the current socket (missed heartbeat): it may never fire `close`, so detach it,
   * show the disconnected state and reconnect with a fresh token.
   */
  private dropSocket(reason: string): void {
    const ws = this.ws;
    this.ws = null;
    this.connecting = false;
    this.clearTimers();
    if (ws !== null) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      try {
        ws.close(4000, "heartbeat timeout");
      } catch {
        /* already closing */
      }
    }
    useConnection.getState().set({ status: "disconnected", gwRttMs: null, lastError: reason });
    this.scheduleReconnect();
  }

  /** Send a request envelope; returns the req_id so callers can match `CommandResult`. */
  send(body: Envelope["body"]): string {
    const reqId = `r${String(++this.reqCounter)}`;
    const env = create(EnvelopeSchema, { seq: 0n, reqId, body });
    this.rawSend(env);
    return reqId;
  }

  private rawSend(env: Envelope): void {
    if (this.ws?.readyState !== WebSocket.OPEN) throw new Error("trading stream not open");
    this.ws.send(toBinary(EnvelopeSchema, env));
  }

  private isStopped(): boolean {
    return this.stopped;
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;
    this.connecting = true;
    const store = useConnection.getState();
    store.set({ status: "connecting", reconnectAttempt: this.policy.attempts, lastError: null });

    let token: string;
    try {
      token = await this.opts.getToken();
    } catch (e) {
      this.connecting = false;
      store.set({ status: "disconnected", lastError: e instanceof Error ? e.message : "token fetch failed" });
      this.scheduleReconnect();
      return;
    }
    // `stop()` may have run during the await; TS keeps the earlier narrowing, so re-read via a call.
    if (this.isStopped()) return;

    let ws: WebSocket;
    try {
      ws = new WebSocket(`${this.opts.baseUrl}/v1/stream/trading`);
    } catch (e) {
      this.connecting = false;
      store.set({ status: "disconnected", lastError: e instanceof Error ? e.message : "socket failed" });
      this.scheduleReconnect();
      return;
    }
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    this.clearWelcomeTimer();
    this.welcomeTimer = setTimeout(() => {
      this.welcomeTimer = null;
      if (this.ws === ws) this.dropSocket("no Welcome from gateway");
    }, WELCOME_TIMEOUT_MS);

    ws.onopen = () => {
      const hello = create(HelloSchema, {
        jwt: token,
        clientVersion: this.opts.clientVersion,
        tabId: this.opts.tabId,
      });
      this.rawSend(create(EnvelopeSchema, { seq: 0n, reqId: "", body: { case: "hello", value: hello } }));
    };

    ws.onmessage = (ev: MessageEvent<ArrayBuffer | string>) => {
      // A socket already given up on (missed heartbeat) must not feed the store.
      if (this.ws !== ws) return;
      this.heartbeat.alive();
      if (!(ev.data instanceof ArrayBuffer)) return;
      let env: Envelope;
      try {
        env = fromBinary(EnvelopeSchema, new Uint8Array(ev.data));
      } catch {
        return;
      }
      this.handle(env);
    };

    ws.onclose = (ev) => {
      // A replaced socket's late close must not touch the new socket's timers or status.
      if (this.ws !== ws) return;
      this.ws = null;
      this.connecting = false;
      this.clearTimers();
      const s = useConnection.getState();
      if (ev.code === CLOSE_UNAUTHENTICATED) {
        s.set({ status: "disconnected", lastError: "unauthenticated (4401)" });
        // A fresh token is fetched on every attempt; keep retrying with backoff in case the
        // API session is still valid and the token simply expired in transit.
      } else {
        s.set({ status: "disconnected", lastError: ev.reason || `closed ${String(ev.code)}` });
      }
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose follows; nothing to do here.
    };
  }

  private handle(env: Envelope): void {
    const store = useConnection.getState();
    const seq = nextLastSeq(store.lastSeq, env);
    if (seq !== store.lastSeq) store.set({ lastSeq: seq });

    switch (env.body.case) {
      case "welcome": {
        const w = env.body.value;
        this.connecting = false;
        this.clearWelcomeTimer();
        // Not a reset: the ladder restarts only once this connection proved stable.
        this.policy.connected(Date.now());
        // A different gateway session or process (boot id, CB6) numbers its events from 1
        // again: our last_seq means nothing there, so resume it from its start.
        const lastSeq = replayAppliedStart(store, w);
        this.pendingApplied.clear();
        this.financeReplayTarget = w.lastSeq;
        store.set({
          financeReady: false,
          appliedSeq: lastSeq,
          status: "connected",
          serverMode: w.serverMode,
          gatewayVersion: w.gatewayVersion,
          sessionId: w.sessionId,
          bootId: w.bootId,
          lastSeq,
          reconnectAttempt: 0,
          lastError: null,
        });
        // Reconnect: replay what we missed. First connect too (last_seq 0): the session may
        // already hold ordered events (accounts, orders, positions) from before this window
        // opened, so replay them from the ring's start, or get a Snapshot when the ring no
        // longer reaches back that far (the Snapshot carries the gateway's last seq, so the next
        // Resume continues from it). `Welcome.last_seq` is the gateway's newest seq; the same
        // session id after a gateway restart restarts at 1, and a last_seq past the ring's end
        // is answered with a Snapshot too.
        this.send({ case: "resume", value: create(ResumeSchema, { lastSeq }) });
        this.heartbeat.start();
        break;
      }
      case "pong": {
        const sentAt = this.pendingPings.get(env.reqId);
        if (sentAt !== undefined) {
          this.pendingPings.delete(env.reqId);
          store.set({
            gwRttMs: Math.round(performance.now() - sentAt),
            rithmicRttMs: env.body.value.rithmicRttMs,
          });
        }
        break;
      }
      default:
        break;
    }
    for (const l of this.listeners) l(env);
    // Never expose cached account finance between Welcome and applying missed invalidations.
    // In particular Snapshot state must reach every listener BEFORE this display barrier opens.
    const appliedStore = useConnection.getState();
    const appliedSeq =
      env.body.case === "welcome"
        ? appliedStore.appliedSeq
        : nextAppliedSeq(appliedStore.appliedSeq, env, this.pendingApplied);
    appliedStore.set({
      appliedSeq,
      financeReady:
        appliedStore.status === "connected" &&
        this.financeReplayTarget !== null &&
        appliedSeq >= this.financeReplayTarget,
    });
  }

  /** One heartbeat ping; false when the socket is not open. */
  private sendPing(): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    const reqId = this.send({ case: "ping", value: create(PingSchema, { tClientMs: BigInt(Date.now()) }) });
    this.pendingPings.set(reqId, performance.now());
    // Drop pings that never got a pong so the map cannot grow unbounded.
    if (this.pendingPings.size > 10) {
      const oldest = this.pendingPings.keys().next().value;
      if (oldest !== undefined) this.pendingPings.delete(oldest);
    }
    return true;
  }

  private clearWelcomeTimer(): void {
    if (this.welcomeTimer !== null) clearTimeout(this.welcomeTimer);
    this.welcomeTimer = null;
  }

  private clearTimers(): void {
    this.clearWelcomeTimer();
    this.heartbeat.stop();
    this.pendingPings.clear();
    this.pendingApplied.clear();
    this.financeReplayTarget = null;
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer !== null) return;
    const delay = this.policy.nextDelayMs(Date.now());
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }
}
